import {
  CustomerDeliveryStatus,
  Order,
} from "../../../Redux/custmoer_orders/types";

/* ------------------------------------------------------------------ */
/* Shared types (ported from demo UserOrdersData / UserFoodOrdersData) */
/* ------------------------------------------------------------------ */

export type OrderRecordStatus =
  | "Delivered"
  | "Processing"
  | "Shipped"
  | "Cancelled"
  | "Refunded";

export type OrderRecordItem = {
  productId: string;
  productName: string;
  description: string;
  image: string;
  quantity: number;
  price: number;
  sellerName: string;
  returnWindowText: string;
};

export type OrderRecord = {
  orderId: string;
  orderNo: string;
  placedAt: string;
  placedTime: string;
  total: number;
  shipTo: string;
  status: OrderRecordStatus;
  statusDate: string;
  statusTitle: string;
  statusMessage: string;
  items: OrderRecordItem[];
  refundAmount: number;
  refundStatus: string;
  deliveryStatus: string;
  paymentMethod: string;
  paymentStatus: string;
  invoiceNumber: string;
  purchaseType: string;
  deliveredAt?: string;
  address: {
    name: string;
    line1: string;
    line2?: string;
    landmark?: string;
    city: string;
    state: string;
    postalCode: string;
    country?: string;
    phone: string;
  };
  summary: {
    itemsSubtotal: number;
    shipping: number;
    cashOnDeliveryFee: number;
    tax: number;
    grandTotal: number;
  };
};

export type FoodOrderStage =
  | "placed"
  | "prepared"
  | "out-for-delivery"
  | "delivered"
  | "cancelled";

export type FoodOrderItem = {
  id: string;
  name: string;
  quantity: number;
  price: number;
  isVeg: boolean;
};

export type FoodOrderTrackingStep = {
  id: "placed" | "prepared" | "out-for-delivery" | "delivered";
  label: string;
  time: string;
};

export type FoodOrderBill = {
  itemTotal: number;
  platformFee: number;
  discountLabel?: string;
  discountAmount: number;
  shoppingVoucher?: number;
  salarRuby?: number;
  deliveryFee: number;
  taxes: number;
  paidCash?: number;
  grandTotal: number;
};

export type FoodOrderRecord = {
  id: string;
  restaurantName: string;
  restaurantArea: string;
  restaurantImage: string;
  orderedAt: string;
  orderedTime: string;
  deliveredAt?: string;
  deliveredTime?: string;
  stage: FoodOrderStage;
  stageLabel: string;
  statusMessage: string;
  riderName?: string;
  items: FoodOrderItem[];
  tracking: FoodOrderTrackingStep[];
  bill: FoodOrderBill;
};

/* ------------------------------------------------------------------ */
/* Formatters (ported from demo data files)                            */
/* ------------------------------------------------------------------ */

const toDate = (value?: string): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(value);

export const formatOrderDate = (value?: string) => {
  const date = toDate(value);
  if (!date) return "N/A";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

export const formatLongOrderDate = (value?: string) => {
  const date = toDate(value);
  if (!date) return "N/A";
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

export const formatOrderTime = (value?: string) => {
  const date = toDate(value);
  if (!date) return "";
  return new Intl.DateTimeFormat("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

export const formatDateKey = (value?: string) => {
  const date = toDate(value);
  if (!date) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
};

export const formatFoodCurrency = formatCurrency;

export const formatFoodOrderDate = (value?: string) => formatLongOrderDate(value);

export const formatFoodOrderLongDate = (value?: string) => {
  const date = toDate(value);
  if (!date) return "N/A";
  return new Intl.DateTimeFormat("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
};

export const toClassSlug = (value: string) =>
  value.toLowerCase().trim().replace(/[\s_]+/g, "-");

/* ------------------------------------------------------------------ */
/* Order -> OrderRecord (product orders page)                          */
/* ------------------------------------------------------------------ */

const PRODUCT_IMAGE_FALLBACK = "../../assets/img/placeholder.png";
const FOOD_IMAGE_FALLBACK = "../../assets/img/food/food-1.jpg";

const mapDeliveryStatusToOrderStatus = (
  order: Order,
): { status: OrderRecordStatus; statusTitle: string; statusMessage: string } => {
  const raw = order.deliveryStatus || (order as any).status || "PENDING";
  const deliveredOn = order.shipmentInfo?.deliveredAt;

  switch (raw) {
    case CustomerDeliveryStatus.DELIVERED:
      return {
        status: "Delivered",
        statusTitle: `Delivered ${formatLongOrderDate(deliveredOn || order.orderDate)}`,
        statusMessage: "Package was handed to resident",
      };
    case CustomerDeliveryStatus.OUT_FOR_DELIVERY:
      return {
        status: "Shipped",
        statusTitle: "Out for delivery",
        statusMessage: "Your package will arrive soon",
      };
    case CustomerDeliveryStatus.IN_TRANSIT:
      return {
        status: "Shipped",
        statusTitle: `Shipped ${formatLongOrderDate(order.shipmentInfo?.shippedAt || order.orderDate)}`,
        statusMessage: "Your package is on the way",
      };
    case CustomerDeliveryStatus.CANCELLED:
    case CustomerDeliveryStatus.NOT_DELIVERED:
      return {
        status: "Cancelled",
        statusTitle: "Order cancelled",
        statusMessage: "This order was cancelled",
      };
    default:
      if (
        order.paymentStatus === "Refunded" ||
        order.paymentStatus === "REFUNDED"
      ) {
        return {
          status: "Refunded",
          statusTitle: "Refund completed",
          statusMessage: "Your refund has been processed successfully",
        };
      }
      return {
        status: "Processing",
        statusTitle: "Order is being processed",
        statusMessage: "We are preparing your order for dispatch",
      };
  }
};

export const mapOrderToOrderRecord = (order: Order): OrderRecord => {
  const statusInfo = mapDeliveryStatusToOrderStatus(order);
  const items = order.productDetails || (order as any).items || [];
  const finalAmount = order.finalAmount || {
    amount: 0,
    platformFee: 0,
    deliveryCharges: 0,
    finalPrice: 0,
  };
  const refund = order.refundDetails;
  const addr = order.deliveryAddress;

  return {
    orderId: order.orderId || (order as any).id || "",
    orderNo: order.orderNo || order.orderNumber || "",
    placedAt: order.orderDate || (order as any).createdAt || "",
    placedTime: formatOrderTime(order.orderDate || (order as any).createdAt),
    total: finalAmount.finalPrice || order.payableAmount || 0,
    shipTo:
      order.deliveryFullName || addr?.fullName || "Self",
    status: statusInfo.status,
    statusDate:
      order.shipmentInfo?.deliveredAt ||
      order.shipmentInfo?.shippedAt ||
      order.orderDate ||
      "",
    statusTitle: statusInfo.statusTitle,
    statusMessage: statusInfo.statusMessage,
    items: items.map((item) => ({
      productId: item.productId || "",
      productName: item.productName || "Product",
      description: item.receivedBenefits || item.sku || "",
      image: item.productImage || PRODUCT_IMAGE_FALLBACK,
      quantity: item.quantity || 1,
      price: item.unitPrice || item.totalPrice || 0,
      sellerName: item.sellerName || order.sellerDetails?.sellerName || "",
      returnWindowText: "",
    })),
    refundAmount:
      refund?.refundAmount || (order as any).refundAmount || 0,
    refundStatus:
      refund?.refundStatus || (order as any).refundStatus || "NOT_APPLICABLE",
    deliveryStatus: order.deliveryStatus || (order as any).status || "",
    paymentMethod:
      order.paymentDetails?.onlinePaymentGateway ||
      order.paymentDetails?.paymentInstrument ||
      "Cash",
    paymentStatus: order.paymentStatus || "",
    invoiceNumber: order.invoice || "",
    purchaseType: order.purchaseType || "",
    deliveredAt: order.shipmentInfo?.deliveredAt,
    address: {
      name: addr?.fullName || order.deliveryFullName || "",
      line1: addr?.addressLine1 || order.deliveryAddressLine1 || "",
      line2: addr?.addressLine2 || order.deliveryAddressLine2 || "",
      landmark: addr?.landmark || order.deliveryLandmark || "",
      city: addr?.city || order.deliveryCity || "",
      state: addr?.state || order.deliveryState || "",
      postalCode: addr?.pincode || order.deliveryPincode || "",
      country: addr?.country || order.deliveryCountry || "",
      phone: addr?.mobile || order.deliveryMobile || "",
    },
    summary: {
      itemsSubtotal: order.payableAmount || finalAmount.amount || 0,
      shipping: finalAmount.deliveryCharges || 0,
      cashOnDeliveryFee: 0,
      tax: 0,
      grandTotal: finalAmount.finalPrice || order.payableAmount || 0,
    },
  };
};

/* ------------------------------------------------------------------ */
/* Order -> FoodOrderRecord (food orders page)                         */
/* ------------------------------------------------------------------ */

const FOOD_PREPARED_STATUSES: string[] = [
  CustomerDeliveryStatus.IN_TRANSIT,
  CustomerDeliveryStatus.ORDER_ACCEPTED,
  CustomerDeliveryStatus.PREPARING,
  CustomerDeliveryStatus.ORDER_READY,
  CustomerDeliveryStatus.RIDER_ASSIGNED,
  CustomerDeliveryStatus.RIDER_ARRIVED_AT_RESTAURANT,
];

const FOOD_OUT_FOR_DELIVERY_STATUSES: string[] = [
  CustomerDeliveryStatus.OUT_FOR_DELIVERY,
  CustomerDeliveryStatus.ORDER_PICKED_UP,
  CustomerDeliveryStatus.RIDER_ARRIVED_AT_LOCATION,
];

const FOOD_CANCELLED_STATUSES: string[] = [
  CustomerDeliveryStatus.CANCELLED,
  CustomerDeliveryStatus.NOT_DELIVERED,
];

const STAGE_LABELS: Record<FoodOrderStage, string> = {
  placed: "Order Placed",
  prepared: "Preparing",
  "out-for-delivery": "Out for Delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

export const mapDeliveryStatusToFoodStage = (
  deliveryStatus?: string,
): FoodOrderStage => {
  const status = deliveryStatus || "";
  if (status === CustomerDeliveryStatus.DELIVERED) return "delivered";
  if (FOOD_CANCELLED_STATUSES.includes(status)) return "cancelled";
  if (FOOD_OUT_FOR_DELIVERY_STATUSES.includes(status))
    return "out-for-delivery";
  if (FOOD_PREPARED_STATUSES.includes(status)) return "prepared";
  return "placed";
};

const STAGE_STATUS_MESSAGES: Record<FoodOrderStage, string> = {
  placed: "The restaurant has received your order",
  prepared: "Your food is being prepared",
  "out-for-delivery": "Your order is on the way",
  delivered: "Order delivered successfully",
  cancelled: "This order was cancelled",
};

const buildTrackingSteps = (order: Order): FoodOrderTrackingStep[] => {
  const info = order.shipmentInfo;
  return [
    {
      id: "placed",
      label: "Order Placed",
      time: formatOrderTime(order.orderDate),
    },
    {
      id: "prepared",
      label: "Item Prepared",
      time: formatOrderTime(info?.confirmedAt || info?.acceptedAt),
    },
    {
      id: "out-for-delivery",
      label: "Out for Delivery",
      time: formatOrderTime(info?.shippedAt),
    },
    {
      id: "delivered",
      label: "Order Delivered",
      time: formatOrderTime(info?.deliveredAt),
    },
  ];
};

export const mapOrderToFoodOrderRecord = (order: Order): FoodOrderRecord => {
  const stage = mapDeliveryStatusToFoodStage(
    order.deliveryStatus || (order as any).status,
  );
  const items = order.productDetails || [];
  const finalAmount = order.finalAmount || {
    amount: 0,
    platformFee: 0,
    deliveryCharges: 0,
    finalPrice: 0,
  };
  const offers = order.offersApplied;
  const deliveredAt = order.shipmentInfo?.deliveredAt;

  return {
    id: order.orderNo || order.orderId || (order as any).id || "",
    restaurantName: order.sellerDetails?.sellerName || "Restaurant",
    restaurantArea: order.sellerDetails?.sellerLocation || "",
    restaurantImage:
      items[0]?.productImage || FOOD_IMAGE_FALLBACK,
    orderedAt: order.orderDate || (order as any).createdAt || "",
    orderedTime: formatOrderTime(order.orderDate || (order as any).createdAt),
    deliveredAt,
    deliveredTime: formatOrderTime(deliveredAt),
    stage,
    stageLabel: STAGE_LABELS[stage],
    statusMessage: STAGE_STATUS_MESSAGES[stage],
    items: items.map((item, index) => ({
      id: item.productId || `item-${index}`,
      name: item.productName || "Item",
      quantity: item.quantity || 1,
      price: item.unitPrice || item.totalPrice || 0,
      isVeg: true,
    })),
    tracking: buildTrackingSteps(order),
    bill: {
      itemTotal: finalAmount.amount || order.payableAmount || 0,
      platformFee: finalAmount.platformFee || 0,
      discountLabel: order.couponDetails?.couponCode || offers?.salarCode,
      discountAmount: offers?.totalDiscount || 0,
      shoppingVoucher: offers?.salarShoppingVoucher || 0,
      salarRuby: offers?.salarRuby || 0,
      deliveryFee: finalAmount.deliveryCharges || 0,
      taxes: 0,
      grandTotal: finalAmount.finalPrice || order.payableAmount || 0,
    },
  };
};

export const getFoodOrderItemCount = (order: FoodOrderRecord) =>
  order.items.reduce((acc, item) => acc + (item.quantity || 1), 0);
